import { createContext, use, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'

/** The Supabase session, as the app sees it. */
export interface AuthState {
  /** The session, or `null` when signed out. */
  session: Session | null
  /** `true` until the stored session has been read. */
  loading: boolean
}

const AuthContext = createContext<AuthState>({ session: null, loading: true })

/**
 * Follows the Supabase session. Cached server data is dropped when the user
 * signs out or another user signs in.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<AuthState>({ session: null, loading: true })
  const userId = useRef<string | null>(null)

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null
      if (userId.current !== null && userId.current !== next) queryClient.clear()
      userId.current = next
      setState({ session, loading: false })
    })
    return () => data.subscription.unsubscribe()
  }, [queryClient])

  return <AuthContext value={state}>{children}</AuthContext>
}

/**
 * The current session.
 * @returns The session and whether it is still loading.
 */
export function useAuth(): AuthState {
  return use(AuthContext)
}
